/**
 * Per-unit Bluetooth connection status glyph.
 *
 * Renders a small Bluetooth icon reflecting a specific radio unit's BLE state
 * (identified by ICCID), for at-a-glance cues in detail headers and lists.
 *
 * Three states:
 * - not advertising nearby → renders nothing (the icon's presence means "in range")
 * - advertising but not yet authenticated → muted icon
 * - connected + authenticated → active icon
 *
 * Contains:
 * - BluetoothIndicator — the indicator component (default export)
 */

import Bluetooth from '@mui/icons-material/Bluetooth';
import BluetoothConnected from '@mui/icons-material/BluetoothConnected';
import PropTypes from 'prop-types';
import React from 'react';

import { useBluetooth } from './BluetoothContext';

/**
 * @param {{ iccid: string, fontSize?: string, sx?: object }} props
 */
const BluetoothIndicator = ({ iccid, fontSize, sx }) => {
  // Tolerant match: callsites pass the site's stored ICCID, while connectedIccid
  // and the advertised keys are the reconstructed form — exact equality would
  // misread state. isConnectedToSite / isSiteAdvertising handle the prefix diff.
  const { isConnectedToSite, isSiteAdvertising } = useBluetooth();

  if (isConnectedToSite(iccid)) {
    return <BluetoothConnected color="primary" fontSize={fontSize} sx={sx} aria-label="Bluetooth connected" />;
  }

  // Only show the muted glyph while the unit is advertising nearby; render
  // nothing otherwise so the icon's mere presence signals the unit is in range.
  if (isSiteAdvertising(iccid)) {
    return (
      <Bluetooth
        fontSize={fontSize}
        sx={{ color: 'action.disabled', ...sx }}
        aria-label="Bluetooth in range, not connected"
      />
    );
  }

  return null;
};

BluetoothIndicator.propTypes = {
  /** ICCID of the radio unit to reflect connection state for. */
  iccid: PropTypes.string.isRequired,
  /** MUI icon fontSize — 'small' | 'medium' | 'large' | 'inherit'. */
  fontSize: PropTypes.string,
  /** sx overrides merged onto the icon. */
  sx: PropTypes.object,
};

BluetoothIndicator.defaultProps = {
  fontSize: undefined,
  sx: undefined,
};

export default BluetoothIndicator;
